import { concreteStreamingMode, isIpPortMode } from './api/v1/helpers'
import {
    CoaxPortMode,
    ComprimatoPortMode,
    InputPort,
    IpPortMode,
    MatroxPortMode,
    NmosPortMode,
    NmosRtpDelivery,
    OutputPort,
    PortMode,
    PortType,
    SrtMode,
    VideonPortMode,
} from './api/v1/types'
import { InputPortDbConfiguration, OutputPortDbConfiguration } from 'common/types'

// Highest valid TCP/UDP port. RTP uses ports above the base (RTCP +1, FEC +2/+4),
// so derived ports must stay within this limit too (EDGE-6632).
export const MAX_PORT_NUMBER = 65535

export const TCP_EDGE_CONTROL_HTTP_PORT = 8081

// TODO: We don't check for conflicts on the thumb port to be backwards compatible with potentially existing services.
// This could cause issues if the thumb appliance is located on the video node.
export const TCP_THUMB_EDGE_CONTROL_HTTP_PORT = 8082

// TODO: EDGE-4269: Use GlobalSettings.ristTunnelBasePort instead of UDP_TUNNEL_PORT_RANGE_START when checking for reserved ports.
export const UDP_TUNNEL_PORT_RANGE_START = 20000
export const UDP_TUNNEL_PORT_RANGE_END = 20999

export const UDP_HANDOVER_PORT_RANGE_START = 30000
export const UDP_HANDOVER_PORT_RANGE_END = 30999

export function nextPortNumber(ports: number[]) {
    const start = UDP_HANDOVER_PORT_RANGE_START
    const end = UDP_HANDOVER_PORT_RANGE_END
    const occupiedPorts = new Set(ports)
    for (let i = start; i < end; i++) {
        // Ensure port is even (might not be the case since older versions did not increment by 2).
        // Typically RTP will be sent on an even-numbered UDP port, with RTCP messages being sent over the next higher odd-numbered port.
        const isEven = i % 2 === 0
        if (!isEven) {
            continue
        }
        // Ensure that there is a spacing of 2 between ports
        if (occupiedPorts.has(i) || occupiedPorts.has(i + 1) || occupiedPorts.has(i - 1)) {
            continue
        }

        return i
    }

    throw new Error(`Failed to allocate handover port. No free ports in range ${start}-${end}`)
}

export function isIpBasedProtocol(mode: PortMode): mode is IpPortMode | NmosPortMode {
    return concreteStreamingMode(mode) in IpPortMode
}
export function isUdpBasedProtocol(
    mode: PortMode | 'tunnel-client' | 'tunnel-server'
): mode is Exclude<IpPortMode, 'rtmp'> | NmosPortMode | 'tunnel-client' | 'tunnel-server' {
    return (
        mode === 'tunnel-client' || mode === 'tunnel-server' || (isIpBasedProtocol(mode) && !isTcpBasedProtocol(mode))
    )
}
export function isTcpBasedProtocol(mode: PortMode | 'tunnel-client' | 'tunnel-server'): mode is IpPortMode.rtmp {
    return mode === IpPortMode.rtmp
}

export function getLocalPortFromPortDbConfiguration(
    configuration: InputPortDbConfiguration | OutputPortDbConfiguration
): number {
    // Rendezvous 'remotePort' is also the local listen port
    if ('remotePort' in configuration && 'srtMode' in configuration && configuration.srtMode === SrtMode.rendezvous)
        return configuration.remotePort
    if ('localPort' in configuration && configuration.localPort) return configuration.localPort
    return 0
}

export function getLocalPortNumbersUsedByTunnelPort(port: {
    type: 'tunnel-client' | 'tunnel-server'
    localPort: number
}): number[] {
    return [port.localPort]
}

function inputPortUsesMulticast(port: InputPort): boolean {
    if (port.mode === NmosPortMode.nmos) {
        return port.resolvedTransport?.delivery === NmosRtpDelivery.multicast
    }
    return 'multicastAddress' in port && !!port.multicastAddress
}

export function getLocalPortNumbersUsedByInputPort(port: InputPort): number[] {
    let localBasePort: number | undefined

    switch (port.mode) {
        case NmosPortMode.nmos:
            localBasePort =
                port.resolvedTransport?.localPort ??
                (port.source.type === 'mpeg-ts' ? port.source.localPort : undefined)
            break
        case IpPortMode.generator:
            if (typeof port.port === 'number') {
                localBasePort = port.port
            }
            break

        case IpPortMode.udp: // fallthrough
        case IpPortMode.rtp: // fallthrough
        case IpPortMode.rist: // fallthrough
        case IpPortMode.rtmp:
            localBasePort = port.port
            break

        case IpPortMode.srt: {
            switch (port.srtMode) {
                case SrtMode.rendezvous:
                    localBasePort = port.remotePort
                    break
                case SrtMode.caller: // fallthrough
                case SrtMode.listener:
                    localBasePort = port.localPort
                    break
            }
            break
        }
    }

    if (!localBasePort) {
        return []
    }

    const protocolMode = concreteStreamingMode(port.mode)
    const isFec = 'fec' in port && !!port.fec
    const isRtcp = port.mode === IpPortMode.rtp && port.rtcp === true
    const isMulticast = inputPortUsesMulticast(port)
    return getLocalPortNumbersUsedByInputProtocol({ mode: protocolMode, localBasePort, isFec, isRtcp, isMulticast })
}

export function getLocalPortNumbersUsedByOutputPort(port: OutputPort): number[] {
    let localBasePort: number | undefined = undefined

    switch (port.mode) {
        case IpPortMode.rtp: // fallthrough
        case IpPortMode.udp: // fallthrough
        case IpPortMode.rist: // fallthrough
            localBasePort = port.localPort
            break

        case IpPortMode.srt: {
            switch (port.srtMode) {
                case SrtMode.rendezvous:
                    localBasePort = port.remotePort
                    break
                case SrtMode.caller: // fallthrough
                case SrtMode.listener:
                    localBasePort = port.localPort
                    break
            }
            break
        }
    }

    return localBasePort
        ? getLocalPortNumbersUsedByOutputProtocol({
              mode: port.mode,
              localBasePort,
          })
        : []
}

export function getLocalPortNumbersUsedByInputProtocol(port: {
    mode: PortMode
    localBasePort: number
    isFec: boolean
    isRtcp: boolean
    isMulticast: boolean
}): number[] {
    const portNumbers = new Set<number>()

    switch (port.mode) {
        case IpPortMode.rist:
            portNumbers.add(port.localBasePort)
            portNumbers.add(port.localBasePort + 1) // RTCP
            break

        case IpPortMode.udp:
            // EDGE-4003: Don't check for conflicts against multicast ports
            if (!port.isMulticast) {
                portNumbers.add(port.localBasePort)
            }
            break

        case IpPortMode.rtp:
            // EDGE-4003: Don't check for conflicts against multicast ports
            if (!port.isMulticast) {
                portNumbers.add(port.localBasePort)
                if (port.isRtcp) {
                    portNumbers.add(port.localBasePort + 1)
                }
                if (port.isFec) {
                    portNumbers.add(port.localBasePort + 2)
                    portNumbers.add(port.localBasePort + 4)
                }
            }
            break

        case IpPortMode.rtmp: // fallthrough
        case IpPortMode.srt: // fallthrough
        case IpPortMode.generator:
            portNumbers.add(port.localBasePort)
            break
    }
    return Array.from(portNumbers)
}

export function getLocalPortNumbersUsedByOutputProtocol(port: { mode: PortMode; localBasePort: number }): number[] {
    const portNumbers = new Set<number>()

    switch (port.mode) {
        case IpPortMode.udp: // fallthrough
        case IpPortMode.rtp: // fallthrough
        case IpPortMode.srt: // fallthrough
        case IpPortMode.rist:
            portNumbers.add(port.localBasePort)
            break
    }
    return Array.from(portNumbers)
}

export function physicalPortTypeMatchesPortMode(
    physicalPortType: PortType,
    mode: (InputPort | OutputPort)['mode']
): boolean {
    switch (physicalPortType) {
        case PortType.coax:
            return mode in CoaxPortMode || mode in MatroxPortMode || mode === ComprimatoPortMode.comprimatoSdi
        case PortType.ndi:
            return mode === ComprimatoPortMode.comprimatoNdi
        case PortType.videon:
            return mode in VideonPortMode
        case PortType.ip: {
            return isIpPortMode(concreteStreamingMode(mode))
        }
    }
}
